import { add, qs, qsa, remove, listen } from "@fluorescent/dom";
import { Ajaxinate } from "ajaxinate";

import isMobile from "is-mobile";
import { section } from "@/glow";
import ProductItem from "@/lib/product-item";
import filterHandler from "@/lib/filters/filter-handler";
import FilterSidebar from "@/lib/filters/filter-sidebar";
import FilterDrawer from "@/lib/filters/filter-drawer";
import filterTopBar from "@/lib/filters/filter-topbar";
import { emit } from "@/glow/events";
import { animateCollectionAndSearch, shouldAnimate } from "@/lib/animation";
import { onFilterChange } from "@/lib/filters/events";
import StickyScroll from "@/lib/sticky-scroll";

const selectors = {
  infiniteScrollContainer: ".collection__infinite-container",
  infiniteScrollTrigger: ".collection__infinite-trigger",
  partial: "[data-partial]",
  filterSidebar: "[data-filter-sidebar]",
  filterDrawer: "[data-filter-drawer]",
  filterTopBar: "[data-filter-topbar]",
  sidebarToggle: "[data-sidebar-toggle]",
  drawerToggle: "[data-drawer-toggle]",
  loader: ".collection__loading",
  paginationItemCount: "[data-pagination-item-count]",
  productItems: ".product-item",
};

const classes = {
  active: "is-active",
  hideProducts: "animation--container-hide",
  loading: "is-loading",
};

const { strings } = window.theme;

section("collection", {
  infiniteScroll: null,
  focusTrap: null,
  events: [],

  onLoad() {
    const { collectionItemCount, paginationType, enableStickyContainer } =
      this.container.dataset;

    if (!parseInt(collectionItemCount)) return;

    this.partial = qs(selectors.partial, this.container);
    this.filterSidebarEl = qs(selectors.filterSidebar, this.container);
    this.filterDrawerEl = qs(selectors.filterDrawer, this.container);
    this.filterTopBarEl = qs(selectors.filterTopBar, this.container);
    this.paginationItemCount = qs(
      selectors.paginationItemCount,
      this.container
    );
    this.loadingEl = qs(selectors.loader, this.container);

    // filtering
    this._initFiltering();

    // Ininite scroll
    this.paginationType = paginationType;
    this.paginated = this.paginationType === "paginated";

    this.infiniteScrollTrigger = qs(
      selectors.infiniteScrollTrigger,
      this.container
    );

    if (!this.paginated) {
      this._initInfiniteScroll();
    }

    this.productItem = ProductItem(this.container);

    if (shouldAnimate(this.container)) {
      this.animateCollection = animateCollectionAndSearch(this.container);
    }

    // Apply loading animation as soon as a filter has changed
    onFilterChange(() => {
      add(this.loadingEl, classes.active);
      add(this.filterDrawerEl, classes.loading);
    });

    if (enableStickyContainer == "true" && !isMobile()) {
      this.stickyScroll = StickyScroll(this.container, {
        styledEl: this.filterSidebarEl,
      });
    }
  },

  _initFiltering() {
    this.filterTopBar = filterTopBar(this.container);
    this.filterHandler = filterHandler({
      container: this.container,
      renderCB: this._renderView.bind(this),
    });

    if (this.filterSidebarEl) {
      this.filterSidebar = FilterSidebar(this.container);
      this.sidebarToggleEl = qs(selectors.sidebarToggle, this.container);

      this.events.push(
        listen(this.sidebarToggleEl, "click", evt => {
          this.filterSidebar?.toggleSidebar(evt);
        })
      );
    }
    if (this.filterDrawerEl) {
      this.filterDrawer = FilterDrawer(this.container);
      this.drawerToggleEl = qs(selectors.drawerToggle, this.container);

      this.events.push(
        listen(this.drawerToggleEl, "click", evt => {
          this.filterDrawer?.toggleDrawer(evt);
        })
      );
    }
  },

  _initInfiniteScroll() {
    const infiniteScrollOptions = {
      container: selectors.infiniteScrollContainer,
      pagination: selectors.infiniteScrollTrigger,
      loadingText: "Loading...",
      callback: () => {
        this.productItem && this.productItem.unload();
        this.productItem = ProductItem(this.container);
        this.animateCollection?.infiniteScrollReveal();
        this._updatePaginationCount();

        emit("collection:updated");
      },
    };

    if (this.paginationType === "click") {
      infiniteScrollOptions.method = "click";
    }

    this.infiniteScroll = new Ajaxinate(infiniteScrollOptions);
  },

  _updatePaginationCount() {
    const productItemCount = qsa(selectors.productItems, this.container).length;
    const viewing = strings.pagination.viewing
      .replace("{{ of }}", `${productItemCount}`)
      .replace("{{ total }}", this.partial.dataset.collectionProductsCount);
    this.paginationItemCount.innerHTML = `${viewing} ${strings.pagination.products}`;
  },

  _renderView(searchParams, updateHistory = true) {
    const url = `${window.location.pathname}?section_id=${this.container.dataset.sectionId}&${searchParams}`;

    add(this.partial, classes.hideProducts);
    add(this.loadingEl, classes.active);
    this.filterDrawerEl && add(this.filterDrawerEl, classes.loading);

    fetch(url)
      .then(res => res.text())
      .then(res => {
        if (updateHistory) {
          this._updateURLHash(searchParams);
        }

        const doc = new DOMParser().parseFromString(res, "text/html");
        const updatedPartial = qs(selectors.partial, doc);
        this.partial.innerHTML = updatedPartial.innerHTML;
        this.partial.dataset.collectionProductsCount =
          updatedPartial.dataset.collectionProductsCount;
        this.animateCollection?.updateContents();

        if (!this.paginated && this.infiniteScrollTrigger) {
          this.infiniteScrollTrigger.innerHTML = "";

          this._initInfiniteScroll();
        }

        this.filterSidebar && this.filterSidebar.renderFilters(doc);
        this.filterDrawer && this.filterDrawer.renderFilters(doc);
        this.filterTopBar && this.filterTopBar.renderFilters(doc);
        this.productItem && this.productItem.unload();
        this.productItem = ProductItem(this.container);
        this.paginationItemCount = qs(
          selectors.paginationItemCount,
          this.container
        );

        remove(this.loadingEl, classes.active);
        this.filterDrawerEl && remove(this.filterDrawerEl, classes.loading);
        emit("collection:updated");
      });
  },

  _updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  },

  onUnload() {
    this.infiniteScroll && this.infiniteScroll.destroy();
    this.filterHandler && this.filterHandler.unload();
    this.filterSidebar && this.filterSidebar.unload();
    this.filterDrawer && this.filterDrawer.unload();
    this.filterTopBar && this.filterTopBar.unload();
    this.filtering && this.filtering.unload();
    this.productItem && this.productItem.unload();
    this.animateCollection?.destroy();
    this.stickyScroll?.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
  },
});
